import { useRef } from "react";
import { motion } from "framer-motion";
import { ImageAttachmentPreviewWeb } from "@/pages/agent-interfaces/live-agent/components/ImageAttachmentPreviewWeb";
import { GenieChatInputWeb } from "@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb";

/**
 * Bottom text input that stays above the iOS/Android virtual keyboard on mobile.
 */
export interface MobileAwareBottomInputProps {
  isMobile: boolean;
  inlineMessage: string;
  setInlineMessage: (v: string) => void;
  onSend: () => void;
  attachments: Array<{
    id: string;
    file: File;
    previewUrl: string;
    status: "analyzing" | "ready" | "error";
    analysisText?: string;
  }>;
  removeAttachment: (id: string) => void;
  updateAttachmentAnalysis?: (id: string, text: string) => void;
}

export const MobileAwareBottomInput = ({
  isMobile,
  inlineMessage,
  setInlineMessage,
  onSend,
  attachments,
  removeAttachment,
  updateAttachmentAnalysis,
}: MobileAwareBottomInputProps) => {
  const prevOverflowRef = useRef<string | null>(null);

  const handleFocusCapture = () => {
    if (!isMobile) return;
    if (prevOverflowRef.current == null) {
      prevOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
  };

  const handleBlurCapture = () => {
    if (!isMobile) return;
    if (prevOverflowRef.current != null) {
      document.body.style.overflow = prevOverflowRef.current;
      prevOverflowRef.current = null;
    }
  };

  return (
    <motion.div
      key="inline-chat-input"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="pointer-events-none flex justify-center px-4"
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
      style={
        isMobile
          ? {
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 12,
              zIndex: 40,
              paddingBottom: "max(0px, env(safe-area-inset-bottom))",
            }
          : {
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 24,
            }
      }
    >
      <div className="pointer-events-auto w-full max-w-md">
        {attachments.length > 0 && (
          <ImageAttachmentPreviewWeb
            attachments={attachments.map((att) => ({
              id: att.id,
              previewUrl: att.previewUrl,
              status: att.status,
              analysisText: att.analysisText,
            }))}
            onRemove={removeAttachment}
            onUpdateAnalysis={updateAttachmentAnalysis}
          />
        )}
        <GenieChatInputWeb
          value={inlineMessage}
          onChange={setInlineMessage}
          onSend={onSend}
          hasAttachments={attachments.length > 0}
          disabled={attachments.some((attachment) => attachment.status === "analyzing")}
          onRemoveAttachment={removeAttachment}
          onOpenFilePicker={() => {
            const input = document.getElementById("genie-chat-attachments") as HTMLInputElement | null;
            if (input) {
              input.click();
            }
          }}
        />
      </div>
    </motion.div>
  );
};

export default MobileAwareBottomInput;
